import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, GraduationCap, Mic, Shield, Users, type LucideIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/cn';

/** The four portals, one level (owner, 2026-10-15). */
export type AppRole = 'parent' | 'muqri' | 'student' | 'admin';

/** The order the owner gave: والد · معلم · طالب · مشرف. */
export const ROLE_ORDER: AppRole[] = ['parent', 'muqri', 'student', 'admin'];

/** Set once the hint has been seen; it never shows again in this browser. */
export const ROLE_HINT_KEY = 'fusilat-role-hint-seen';

const readHintSeen = () => {
  try { return localStorage.getItem(ROLE_HINT_KEY) === '1'; } catch { return true; }
};

const ICON: Record<AppRole, LucideIcon> = {
  parent: Users,
  muqri: Mic,
  student: GraduationCap,
  admin: Shield,
};

/**
 * The role button, as in Namat: the current role's icon, its NAME and a
 * chevron. It drops a menu down from itself with every role this account
 * holds and a tick on the current one; picking one switches portal in place.
 * The phone app has the same button.
 *
 * The first time an account with more than one role sees it, a small bubble
 * under it says «بدّل بين أدوارك من هنا» — App Review once could not find the
 * teacher side from the pupil side. It goes for good when clicked or when the
 * button is used.
 *
 * It renders NOTHING for a single role: almost every user is a طالب and
 * nothing else, and a button with one choice is useless chrome.
 */
export function RoleSwitcher({
  available, current, onSelect,
}: {
  available: AppRole[];
  current: AppRole;
  onSelect: (role: AppRole) => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const roles = ROLE_ORDER.filter((role) => available.includes(role));
  const [hintSeen, setHintSeen] = useState<boolean>(readHintSeen);
  const dismissHint = () => {
    if (hintSeen) return;
    setHintSeen(true);
    try { localStorage.setItem(ROLE_HINT_KEY, '1'); } catch { /* storage refused: it shows again next visit */ }
  };

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  if (roles.length <= 1) return null;

  const CurrentIcon = ICON[current];
  const pick = (role: AppRole) => {
    setOpen(false);
    if (role !== current) onSelect(role);
  };

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        data-testid="role-switcher"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('roles.switchTitle')}: ${t(`roles.${current}`)}`}
        onClick={() => { dismissHint(); setOpen((v) => !v); }}
        className={cn(
          'flex h-10 items-center gap-2 rounded-full border border-hair bg-card ps-1.5 pe-3 text-sm font-semibold text-ink transition-colors',
          'hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        )}
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-tint text-primary">
          <CurrentIcon className="h-4 w-4" strokeWidth={2} />
        </span>
        <span data-testid="role-switcher-current" className="whitespace-nowrap">{t(`roles.${current}`)}</span>
        <ChevronDown className={cn('h-4 w-4 text-helper transition-transform', open && 'rotate-180')} strokeWidth={2} />
      </button>

      {!hintSeen && !open ? (
        <button
          type="button"
          data-testid="role-switcher-hint"
          onClick={dismissHint}
          className="absolute end-0 top-full z-40 mt-2.5 w-48 rounded-[14px] bg-accent px-3 py-2 text-center text-[13px] font-semibold text-accent-foreground shadow-[0_8px_20px_rgba(14,58,66,0.18)]"
        >
          <span aria-hidden="true" className="absolute -top-1.5 end-6 h-3 w-3 rotate-45 bg-accent" />
          <span className="relative">{t('roles.hint')}</span>
        </button>
      ) : null}

      {open ? (
        <div
          role="menu"
          data-testid="role-switcher-menu"
          className="absolute end-0 top-full z-50 mt-2 w-56 rounded-[18px] border border-hair bg-card p-1.5 shadow-[0_12px_30px_rgba(14,58,66,0.16)]"
        >
          {roles.map((role) => {
            const Icon = ICON[role];
            const on = role === current;
            return (
              <button
                key={role}
                type="button"
                role="menuitemradio"
                aria-checked={on}
                data-testid={`role-option-${role}`}
                onClick={() => pick(role)}
                className={cn(
                  'flex h-11 w-full items-center gap-3 rounded-xl px-2 text-start text-[15px] text-ink transition-colors',
                  on ? 'bg-tint font-semibold' : 'hover:bg-surface',
                )}
              >
                <span className={cn('flex h-8 w-8 items-center justify-center rounded-full', on ? 'bg-accent text-ink' : 'bg-surface text-primary')}>
                  <Icon className="h-4 w-4" strokeWidth={1.9} />
                </span>
                <span className="flex-1">{t(`roles.${role}`)}</span>
                {on ? <Check className="h-4 w-4 text-primary" strokeWidth={2.2} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
